﻿import { h, FunctionComponent, ComponentChildren } from 'preact';
import { memo } from 'preact/compat';
import styles from './KPIVisualiser.module.scss';
import { useLocalized } from './KPIVisualiser_Context';
import { ENTRY_HEIGHT } from './Utils';

interface Props {
    hidden: boolean;
}

export const KpiVisualiser_Key: FunctionComponent<Props> = memo(({ hidden }) => {
    return (
        <div className={`${styles.visualiserKey} ${hidden ? styles.hidden : ''}`}>
            <h3>{useLocalized("lbl:key_header", "Key")}</h3>

            <table>
                <tbody>
                    <Header>{useLocalized("lbl:key_shapesHeader", "Shapes")}</Header>

                    <Row description={useLocalized("lbl:key_box_desc", "The box defines the KPI's total elapsed time.")}>
                        <BoxIcon label={useLocalized("lbl:key_box", "Box")} />
                    </Row>
                    <Row description={useLocalized("lbl:key_tail_desc", "The end bar of the tail indicates the contractual or agreed KPI deadline.")}>
                        <TailIcon label={useLocalized("lbl:key_tail", "Tail")} />
                    </Row>
                    <Row description={useLocalized("lbl:leading_key_tail_desc", "The leading tail indicates the created date and time of the KPI.")}>
                        <LeadingTailIcon label={useLocalized("lbl:leading_key_tail", "Leading Tail")} />
                    </Row>

                    <Header>{useLocalized("lbl:key_coloursHeader", "Colours")}</Header>

                    <Row description={useLocalized("lbl:key_blackBorder_desc", "A KPI that is currently active.")}>
                        <BoxIcon label={useLocalized("lbl:key_blackBorder", "Black Border")} />
                    </Row>
                    <Row description={useLocalized("lbl:key_green_desc", "A KPI that was completed on time or early.")}>
                        <BoxIcon label={useLocalized("lbl:key_green", "Green")} boxClass={styles.completed} />
                    </Row>
                    <Row description={useLocalized("lbl:key_red_desc", "A KPI that was completed late.")}>
                        <BoxIcon label={useLocalized("lbl:key_red", "Red")} boxClass={styles.late} />
                    </Row>
                    <Row description={useLocalized("lbl:key_deepred_desc", "The breached segment of a late KPI that goes beyond the deadline.")}>
                        <BreachBoxIcon label={useLocalized("lbl:key_deepred", "Deep Red")} boxClass={styles.late} />
                    </Row>
                    <Row description={useLocalized("lbl:key_faded_desc", "A predicted future KPI.")}>
                        <BoxIcon label={useLocalized("lbl:key_faded", "Faded/Light Grey")} boxClass={styles.future} />
                    </Row>
                    <Row description={useLocalized("lbl:key_frozen_desc", "The time the KPI was spent frozen.")}>
                        <FrozenBoxIcon label={useLocalized("lbl:key_frozen", "Light Blue with Hashing")} />
                    </Row>
                </tbody>
            </table>
        </div>
    );
});

const Header: FunctionComponent = memo(({ children }) => {
    return (
        <tr>
            <th colSpan={2}>
                {children}
            </th>
        </tr>
    );
});

interface RowProps {
    children: ComponentChildren;
    description: any;
}

const Row: FunctionComponent<RowProps> = memo(({ children, description }) => {
    return (
        <tr>
            <td>
                {children}
            </td>
            <td>
                {description}
            </td>
        </tr>
    );
});

interface BoxIconProps {
    label: string;
    boxClass?: string;
}

const SVG_ICON_EXTERNAL_WIDTH = 158.0;
const SVG_ICON_INTERNAL_WIDTH = SVG_ICON_EXTERNAL_WIDTH - 8.0;
const SVG_ICON_PADDING = 4.0;

const BoxIcon: FunctionComponent<BoxIconProps> = memo(({ label, boxClass }) => {
    return (
        <svg width={SVG_ICON_EXTERNAL_WIDTH} height={ENTRY_HEIGHT + 8} className={styles.kpiEntry}>
            <g className={boxClass}>
                <rect x={SVG_ICON_PADDING} y={SVG_ICON_PADDING} width={SVG_ICON_INTERNAL_WIDTH} height={ENTRY_HEIGHT} className={styles.containerBox} />
                <rect x={SVG_ICON_PADDING} y={SVG_ICON_PADDING} width={SVG_ICON_INTERNAL_WIDTH} height={ENTRY_HEIGHT} className={styles.containerBoxStroke} />
                <text x={SVG_ICON_INTERNAL_WIDTH / 2.0 + SVG_ICON_PADDING} y={(ENTRY_HEIGHT) / 2.0} alignment-baseline="mathematical" text-anchor="middle">
                    {label}
                </text>
            </g>
        </svg>
    );
});

const BreachBoxIcon: FunctionComponent<BoxIconProps> = memo(({ label, boxClass }) => {
    return (
        <svg width={SVG_ICON_EXTERNAL_WIDTH} height={ENTRY_HEIGHT + 8} className={styles.kpiEntry}>
            <g className={boxClass}>
                <rect x={SVG_ICON_PADDING} y={SVG_ICON_PADDING} width={SVG_ICON_INTERNAL_WIDTH} height={ENTRY_HEIGHT} className={styles.breachBox} />
                <text x={SVG_ICON_INTERNAL_WIDTH / 2.0 + SVG_ICON_PADDING} y={(ENTRY_HEIGHT) / 2.0} alignment-baseline="mathematical" text-anchor="middle">
                    {label}
                </text>
            </g>
        </svg>
    );
});

const FrozenBoxIcon: FunctionComponent<BoxIconProps> = memo(({ label, boxClass }) => {
    return (
        <svg width={SVG_ICON_EXTERNAL_WIDTH} height={ENTRY_HEIGHT + 8} className={styles.kpiEntry}>
            <g className={boxClass}>
                <rect x={SVG_ICON_PADDING} y={SVG_ICON_PADDING} width={SVG_ICON_INTERNAL_WIDTH} height={ENTRY_HEIGHT} className={styles.frozenBox} />
                <text x={SVG_ICON_INTERNAL_WIDTH / 2.0 + SVG_ICON_PADDING} y={(ENTRY_HEIGHT) / 2.0} alignment-baseline="mathematical" text-anchor="middle">
                    {label}
                </text>
            </g>
        </svg>
    );
});

interface TailIconProps {
    label: string;
}

const TailIcon: FunctionComponent<TailIconProps> = memo(({ label }) => {
    const lineY = ENTRY_HEIGHT / 2.0 + SVG_ICON_PADDING;

    return (
        <svg width={SVG_ICON_EXTERNAL_WIDTH} height={ENTRY_HEIGHT + 8} className={styles.kpiEntry}>
            <line x1={SVG_ICON_PADDING} y1={lineY} x2={SVG_ICON_INTERNAL_WIDTH} y2={lineY} class={styles.entryTailLine} />
            <line x1={SVG_ICON_INTERNAL_WIDTH} y1={SVG_ICON_PADDING} x2={SVG_ICON_INTERNAL_WIDTH} y2={ENTRY_HEIGHT + SVG_ICON_PADDING} class={styles.entryTailBar} />
            <text x={SVG_ICON_INTERNAL_WIDTH / 2.0 + SVG_ICON_PADDING} y={ENTRY_HEIGHT / 2.0 - 8.0} alignment-baseline="mathematical" text-anchor="middle">
                {label}
            </text>
        </svg>
    );
});

const LeadingTailIcon: FunctionComponent<TailIconProps> = memo(({ label }) => {
    const lineY = ENTRY_HEIGHT / 2.0 + SVG_ICON_PADDING;

    return (
        <svg width={SVG_ICON_EXTERNAL_WIDTH} height={ENTRY_HEIGHT + 8} className={styles.kpiEntry}>
            <line x1={SVG_ICON_PADDING} y1={SVG_ICON_PADDING} x2={SVG_ICON_PADDING} y2={ENTRY_HEIGHT + SVG_ICON_PADDING} class={styles.entryTailBar} />
            <line x1={SVG_ICON_PADDING} y1={lineY} x2={SVG_ICON_INTERNAL_WIDTH} y2={lineY} class={styles.entryTailLine} />
            <text x={SVG_ICON_INTERNAL_WIDTH / 2.0 + SVG_ICON_PADDING} y={ENTRY_HEIGHT / 2.0 - 8.0} alignment-baseline="mathematical" text-anchor="middle">
                {label}
            </text>
        </svg>
    );
});
